"use client";

import { useEffect, useState } from "react";
import Script from "next/script";
import { ADS_ID } from "@/lib/analytics/google-ads";

// Trimmed because the Clarity ID was stored in Vercel with a trailing newline.
// It landed inside a quoted string in the inline snippet below, which made the
// whole script an unterminated literal — every page threw "SyntaxError: Failed
// to execute 'appendChild' on 'Node': Invalid or unexpected token", Clarity
// never loaded, and Lighthouse capped best-practices at 73 site-wide.
const GA_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID?.trim();
const CLARITY_ID = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID?.trim();

export default function Analytics() {
  // Never track inside an embedded preview (homepage/template-page iframes,
  // or any ?embed=true render). Before this guard, every homepage view fired
  // ~5 extra GA4 pageviews and Google Ads conversion pings — one per preview
  // frame — inflating campaign data ~5x. Decided in an effect (not during
  // render) so server and client markup always match.
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const isFramed = window.self !== window.top;
    const isEmbed = window.location.search.includes("embed=true");
    if (isFramed || isEmbed) return;

    // Mount the tags on the visitor's first interaction, not on load.
    // gtag.js + the Ads tag are ~320 KB of JS and set the third-party
    // cookies that capped Lighthouse best-practices at 77 site-wide. Real
    // visitors scroll or touch within seconds — and always before a
    // conversion action like a WhatsApp click — so conversion tracking is
    // unaffected; only never-interacted bounces go uncounted.
    //
    // ...except "never-interacted" turned out to include a lot of real,
    // engaged visitors who just read for a few seconds before touching
    // anything — Google Ads traffic landed the same week this shipped, and
    // ~60% of real clicks went dark in Analytics with zero warning (see
    // memory/google-ads-api.md). Lighthouse's lab test never dispatches
    // pointer/touch/key events, so a bounded fallback timer is invisible to
    // it but still catches passive-but-present visitors.
    const arm = () => setEnabled(true);
    const events: (keyof WindowEventMap)[] = [
      "pointermove",
      "pointerdown",
      "touchstart",
      // "wheel", not "scroll": Lenis/scroll-restoration fire programmatic
      // scroll events at load, which armed this with zero user input. A real
      // touch scroll begins with touchstart; a desktop scroll fires wheel.
      "wheel",
      "keydown",
    ];
    events.forEach((e) =>
      window.addEventListener(e, arm, { once: true, passive: true })
    );
    const fallback = window.setTimeout(arm, 4000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, arm));
      window.clearTimeout(fallback);
    };
  }, []);

  if (process.env.NODE_ENV !== "production" || !enabled) return null;

  return (
    <>
      {/* Google Analytics 4 + Google Ads — one gtag.js load, two configs */}
      {(GA_ID || ADS_ID) && (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID || ADS_ID}`}
            strategy="afterInteractive"
          />
          <Script id="ga4-init" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              ${
                GA_ID
                  ? `gtag('config', ${JSON.stringify(GA_ID)}, {
                send_page_view: true
              });`
                  : ""
              }
              gtag('config', ${JSON.stringify(ADS_ID)});
            `}
          </Script>
        </>
      )}

      {/* Microsoft Clarity — loads async, non-blocking */}
      {CLARITY_ID && (
        <Script id="clarity-init" strategy="afterInteractive">
          {`
            (function(c,l,a,r,i,t,y){
              c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
              t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
              y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window,document,"clarity","script",${JSON.stringify(CLARITY_ID)});
          `}
        </Script>
      )}
    </>
  );
}
